/**
 * TrustedForm Runtime Integration Hook (MA-OPS-061)
 * 
 * Handles TrustedForm certificate capture inside the funnel widget/iframe context.
 * 
 * Features:
 * - Loads TrustedForm script when compliance settings enable it
 * - Captures certificate URL when available
 * - Provides certificate to lead submission payload
 * - Handles missing certificate based on funnel settings
 * 
 * Architecture:
 * - Widget loads TrustedForm script independently of parent page
 * - Captures certificate for leads submitted within the iframe
 * - Supports custom TrustedForm account configuration
 * 
 * MA-COMPLIANCE-007: Uses trustedformMode as single source of truth
 * - "off" = TrustedForm disabled
 * - "capture_only" = Capture for records only
 * - "enforce" = Full compliance enforcement
 */

import { useState, useEffect, useCallback, useRef } from 'react';

// TrustedForm configuration defaults
const DEFAULT_TF_FIELD_NAME = 'trustedform_cert_url';
const TF_SCRIPT_URL = 'https://api.trustedform.com/trustedform.js?field=xxTrustedFormCertUrl&l=';

/**
 * TrustedForm Mode Options (MA-COMPLIANCE-007)
 * Single source of truth for TrustedForm behavior
 */
export const TrustedFormMode = {
  OFF: 'off',               // TrustedForm disabled
  CAPTURE_ONLY: 'capture_only', // Capture for records, no enforcement
  ENFORCE: 'enforce',       // Full compliance enforcement
};

/**
 * TrustedForm Runtime Hook
 * 
 * @param {Object} complianceSettings - Funnel compliance settings
 * @param {string} complianceSettings.trustedformMode - TrustedForm mode (off|capture_only|enforce)
 * @param {string} complianceSettings.trustedFormAccountId - Custom TF account (optional)
 * @param {string} complianceSettings.trustedFormFieldName - Custom field name (optional)
 * @param {string} externalCertUrl - Certificate URL from parent page (optional)
 */
export function useTrustedFormRuntime(complianceSettings = {}, externalCertUrl = null) {
  const [certUrl, setCertUrl] = useState(externalCertUrl || null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const scriptRef = useRef(null);
  const pollIntervalRef = useRef(null);
  
  // MA-COMPLIANCE-007: Handle null complianceSettings and normalize to mode-based
  const safeComplianceSettings = complianceSettings || {};
  
  // MA-COMPLIANCE-007: trustedformMode is the SINGLE source of truth
  // Default to 'off' if not specified
  const trustedformMode = safeComplianceSettings.trustedformMode || TrustedFormMode.OFF;
  
  // Derived: is TrustedForm enabled (mode !== 'off')
  const isTrustedFormEnabled = trustedformMode !== TrustedFormMode.OFF;
  
  const {
    trustedFormAccountId,
    trustedFormFieldName = DEFAULT_TF_FIELD_NAME,
  } = safeComplianceSettings;

  /**
   * Load TrustedForm script into the widget/iframe
   * MA-EMBED-001: Skip loading if external cert was provided from parent
   */
  const loadTrustedFormScript = useCallback(() => {
    // Don't load if already loaded or disabled
    if (!isTrustedFormEnabled || scriptRef.current) {
      return;
    }
    
    // MA-EMBED-001: If parent page already provided a cert URL, don't load TrustedForm again
    // This prevents the "Lock found" warning from TrustedForm detecting multiple instances
    if (externalCertUrl) {
      console.log('[TrustedForm] Using certificate from parent page, skipping widget script load');
      setCertUrl(externalCertUrl);
      setIsLoaded(true);
      return;
    }

    try {
      // Build script URL with optional custom account ID
      let scriptUrl = TF_SCRIPT_URL;
      if (trustedFormAccountId) {
        scriptUrl += trustedFormAccountId;
      }

      // Create and append script
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = scriptUrl;
      
      script.onload = () => {
        console.log('[TrustedForm] Script loaded successfully in widget runtime');
        setIsLoaded(true);
        setLoadError(null);
        
        // Start polling for certificate URL
        startCertificatePolling();
      };
      
      script.onerror = (error) => {
        console.error('[TrustedForm] Failed to load script:', error);
        setLoadError('Failed to load TrustedForm script');
        setIsLoaded(false);
      };

      document.body.appendChild(script);
      scriptRef.current = script;
      
      console.log('[TrustedForm] Loading script in widget runtime...');
    } catch (error) {
      console.error('[TrustedForm] Error loading script:', error);
      setLoadError(error.message);
    }
  }, [isTrustedFormEnabled, trustedFormAccountId, externalCertUrl]);

  /**
   * Poll for certificate URL from TrustedForm
   * TrustedForm populates a hidden input field with the certificate URL
   */
  const startCertificatePolling = useCallback(() => {
    // Clear existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    const checkForCertificate = () => {
      // Check for the hidden input that TrustedForm creates
      const tfInput = document.querySelector('input[name="xxTrustedFormCertUrl"]');
      if (tfInput && tfInput.value) {
        setCertUrl(tfInput.value);
        console.log('[TrustedForm] Certificate captured:', tfInput.value.substring(0, 50) + '...');
        
        // Stop polling once we have the cert
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        return;
      }

      // Also check global TrustedForm object
      if (typeof window !== 'undefined' && window.TrustedForm) {
        const url = window.TrustedForm.certificate_url;
        if (url) {
          setCertUrl(url);
          console.log('[TrustedForm] Certificate from global:', url.substring(0, 50) + '...');
          
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }
      }
    };

    // Check immediately, then poll every 500ms
    checkForCertificate();
    pollIntervalRef.current = setInterval(checkForCertificate, 500);

    // Stop polling after 30 seconds (TrustedForm should load by then)
    setTimeout(() => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }, 30000);
  }, []);

  /**
   * Get certificate URL (prefer external if provided)
   */
  const getCertificateUrl = useCallback(() => {
    return externalCertUrl || certUrl;
  }, [externalCertUrl, certUrl]);

  /**
   * Validate certificate presence and determine lead eligibility
   * MA-COMPLIANCE-007: Uses mode-based logic
   * - "off" = Always valid, no cert required
   * - "capture_only" = Always valid, cert optional
   * - "enforce" = Cert required for compliance
   */
  const validateCertificate = useCallback(() => {
    const currentCertUrl = getCertificateUrl();
    
    if (trustedformMode === TrustedFormMode.OFF) {
      // TrustedForm OFF - always valid, no cert captured
      return {
        isValid: true,
        mode: trustedformMode,
        certUrl: null,
        complianceFlags: {
          trustedform_mode: trustedformMode,
          trustedform_present: false,
        },
      };
    }
    
    if (trustedformMode === TrustedFormMode.CAPTURE_ONLY) {
      // CAPTURE_ONLY - always valid, cert captured for records
      return {
        isValid: true,
        mode: trustedformMode,
        certUrl: currentCertUrl,
        complianceFlags: {
          trustedform_mode: trustedformMode,
          trustedform_present: !!currentCertUrl,
          trustedform_captured_in_widget: !externalCertUrl && !!certUrl,
        },
      };
    }

    // ENFORCE mode - cert required
    if (currentCertUrl) {
      return {
        isValid: true,
        mode: trustedformMode,
        certUrl: currentCertUrl,
        complianceFlags: {
          trustedform_mode: trustedformMode,
          trustedform_present: true,
          trustedform_captured_in_widget: !externalCertUrl && !!certUrl,
        },
      };
    }

    // ENFORCE mode with missing cert - invalid
    return {
      isValid: false,
      mode: trustedformMode,
      certUrl: null,
      complianceFlags: {
        trustedform_mode: trustedformMode,
        trustedform_present: false,
        trustedform_flagged: true,
      },
    };
  }, [trustedformMode, getCertificateUrl, externalCertUrl, certUrl]);

  /**
   * Build compliance data for lead submission payload
   * MA-COMPLIANCE-007: Uses mode-based payload
   */
  const buildCompliancePayload = useCallback(() => {
    const validation = validateCertificate();
    const fieldName = trustedFormFieldName || DEFAULT_TF_FIELD_NAME;
    
    return {
      [fieldName]: validation.certUrl,
      compliance: {
        trustedform: {
          mode: trustedformMode,
          cert_url: validation.certUrl,
          captured_in_widget: !externalCertUrl && !!certUrl,
          ...validation.complianceFlags,
        },
      },
    };
  }, [validateCertificate, trustedformMode, trustedFormFieldName, externalCertUrl, certUrl]);

  // Load script when enabled (mode !== 'off')
  // MA-EMBED-001: Add delay before loading to allow external cert to arrive from parent
  useEffect(() => {
    // If external cert is already provided, use it and skip script load
    if (externalCertUrl) {
      setCertUrl(externalCertUrl);
      setIsLoaded(true);
      return;
    }
    
    // MA-EMBED-001: Delay script load to give parent page time to send cert via postMessage
    // This prevents the "Lock found" TrustedForm warning from loading twice
    const timeoutId = setTimeout(() => {
      // Double-check external cert hasn't arrived during the delay
      if (isTrustedFormEnabled && !certUrl) {
        loadTrustedFormScript();
      }
    }, 1000); // 1 second delay
    
    return () => {
      clearTimeout(timeoutId);
      // Cleanup polling on unmount
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isTrustedFormEnabled, externalCertUrl, loadTrustedFormScript, certUrl]);

  return {
    // State
    certUrl: getCertificateUrl(),
    isLoaded,
    loadError,
    isEnabled: isTrustedFormEnabled,
    mode: trustedformMode,
    
    // Methods
    getCertificateUrl,
    validateCertificate,
    buildCompliancePayload,
    
    // For debugging
    debug: {
      internalCertUrl: certUrl,
      externalCertUrl,
      scriptLoaded: !!scriptRef.current,
      trustedformMode,
    },
  };
}

export default useTrustedFormRuntime;
